/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTable, useResizeColumns, useFlexLayout, useColumnOrder, useSortBy } from "react-table";
import { Input } from "reactstrap";
import { formatMetricName, formatMetricString } from "src/helpers/commonHelper";
import { toast } from "react-toastify";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { RootState } from "src/slices/store";
import { selectExchangeByName, setSelectedExchangeLineItems, setSelectedExchangeMetrics, selectedExchangesData, setExchangeDataBy, setExchangeHasData, setExchangeEmptyColumns } from "src/slices/Report/reducer";
import { openPanel } from 'src/slices/Compare/reducer';
import ComparePanel from './Compare/ComparePanel';
import DataByDropdown from "src/components/Common/DataByDropdown";
import LineItemDropdown from "src/components/Common/LineItemDropdown";
import TableCustomLoader from "src/components/Common/TableCustomLoader";
import Pagination from "src/components/Common/Pagination";
import useIsMobile from "src/hooks/useIsMobile";
import axiosInstance from "src/common/axiosInstance";
import axios from "axios";
import { tabRegistry, TabRequestContext } from "./tabRegistry";
import ConnectedTab from "./ConnectedTab";
import CustomizeColumns from "src/components/Common/CustomizeColumns/CustomizeColumns";
import { buildCacheKey } from "src/slices/TabData/reducer";
import { useTabAccess } from "src/hooks/useTabAccess";

// Type definitions for table data items
type TableDataItem = {
  line_item_name: string;
  [key: string]: any;
};

// Type definition for report data structure
type ReportData = {
  report_name: string;
  ordered_data: ({
    line_item_id: string;
    line_item_name: string;
  } & Record<string, any>)[];
};

// Type definition for select options
type OptionType = {
  value: string;
  label: string;
};

// Props interface for SortableColumnItem component
interface SortableColumnItemProps {
  metric: string;
  isChecked: boolean;
}

/**
 * Gets unique metrics while ignoring case differences
 * @param {string[]} metrics - Array of metric names
 * @returns {string[]} Array of unique metric names (case-insensitive)
 */
const getUniqueCaseInsensitiveMetrics = (metrics: string[]) => {
  const seen = new Set<string>();
  return metrics.filter(metric => {
    const formatted = formatMetricString(metric);
    if (seen.has(formatted)) return false;
    seen.add(formatted);
    return true;
  });
};

/**
 * ExchangeTable Component - Displays exchange data in a customizable table
 * @param {Object} props - Component props
 * @param {selectedExchangesData} props.individualExchangeData - Exchange data to display
 * @param {number} props.noOfExchanges - Number of exchanges in the report
 */
const ExchangeTable = ({ individualExchangeData, noOfExchanges }: {
  individualExchangeData?: selectedExchangesData;
  noOfExchanges: number;
}) => {
  // State management for table and data
  const [exchangeData, setExchangeData] = useState<any>(individualExchangeData || {});
  const [tableLoading, setTableLoading] = useState(false);
  const [lineItemLoading, setLineItemLoading] = useState(false);
  const [selectedLineItems, setSelectedLineItems] = useState<string[]>([]);
  const [lineItemsOptions, setLineItemsOptions] = useState<any[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [metricsList, setMetricsList] = useState<string[]>([]);
  const filters = useSelector((state: RootState) => state.filters);
  const { selectedOrder, startDate, endDate, dataBy } = filters;
  const orders = useSelector((state: RootState) => state.orders.orders);
  // Initialize with global dataBy value instead of hardcoded 'week'
  const [exchangeReportDataBy, setExchangeReportDataBy] = useState<string>(
    individualExchangeData?.dataBy || dataBy || 'week'
  );
  const [exchangeReportData, setExchangeReportData] = useState<ReportData[]>([]);
  const [tableData, setTableData] = useState<TableDataItem[]>([]);
  const [includeAISummary, setIncludeAISummary] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number>(400);
  const customizeButtonRef = useRef<HTMLButtonElement>(null);
  const userState = useSelector((state: RootState) => state.login);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Server-side sorting state
  const [sortBy, setSortBy] = useState<string>('start_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Tab system state
  const [activeTabId, setActiveTabId] = useState<string>("main");

  // Refs to store latest values for closures
  const sortByRef = useRef(sortBy);
  const sortOrderRef = useRef(sortOrder);
  const currentPageRef = useRef(currentPage);
  const itemsPerPageRef = useRef(itemsPerPage);

  // Refs for refactored effect architecture (prevent infinite loops)
  const isInitializedRef = useRef(false);
  const initializedExchangeNameRef = useRef<string>("");
  const lastReduxMetricsRef = useRef<string>("");
  const selectedMetricsRef = useRef<string[]>([]);
  const metricsListRef = useRef<string[]>([]);
  const exchangeDataRef = useRef<any>(individualExchangeData || {});

  // Update refs when values change
  useEffect(() => { sortByRef.current = sortBy; }, [sortBy]);
  useEffect(() => { sortOrderRef.current = sortOrder; }, [sortOrder]);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  useEffect(() => { itemsPerPageRef.current = itemsPerPage; }, [itemsPerPage]);
  useEffect(() => { selectedMetricsRef.current = selectedMetrics; }, [selectedMetrics]);
  useEffect(() => { metricsListRef.current = metricsList; }, [metricsList]);
  useEffect(() => { exchangeDataRef.current = exchangeData; }, [exchangeData]);

  // Tab system — filter tabs visible for this exchange and user permissions
  const authUser = userState.user.user;
  const tabAccess = useTabAccess();

  const visibleTabs = useMemo(() =>
    tabRegistry.filter(tab => {
      if (tab.tabId === 'main') return true;
      if (tab.exportOnly) return false;
      if (tab.showForExchange && !tab.showForExchange(exchangeData)) return false;
      if (authUser.user_type === 'Client') {
        if (tab.tabId in tabAccess) {
          // Explicitly set to empty array = access removed
          if (Array.isArray(tabAccess[tab.tabId]) && tabAccess[tab.tabId].length === 0) return false;
        } else {
          // Not configured — show only if tab has defaultSelectedMetrics, otherwise hide
          if (!tab.defaultSelectedMetrics) return false;
        }
      }
      return true;
    }),
    [exchangeData, tabAccess, authUser.user_type]
  );

  // Reset to main if active tab becomes invisible
  useEffect(() => {
    if (!visibleTabs.find(t => t.tabId === activeTabId)) {
      setActiveTabId("main");
    }
  }, [visibleTabs, activeTabId]);

  // Build request context for tab system
  const tabRequestContext: TabRequestContext = useMemo(() => ({
    orderId:           selectedOrder,
    startDate:         startDate,
    endDate:           endDate,
    dataBy:            exchangeReportDataBy,
    selectedLineItems: selectedLineItems,
  }), [selectedOrder, startDate, endDate, exchangeReportDataBy, selectedLineItems]);

  // Debounced search implementation
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // Debounced API call for multiple metric selections
  const debounceApiTimeout = useRef<NodeJS.Timeout | null>(null);

  // AbortController for canceling pending API requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounce timeout for date changes
  const dateChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedSetSearchTerm = useCallback((value: string) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 300);
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSetSearchTerm(value);
  }, [debouncedSetSearchTerm]);

  // Cleanup timeouts and abort controllers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      if (debounceApiTimeout.current) {
        clearTimeout(debounceApiTimeout.current);
      }
      if (dateChangeTimeoutRef.current) {
        clearTimeout(dateChangeTimeoutRef.current);
      }
      // Abort any pending API requests on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Redux data and selectors
  const reduxExchangeData = useSelector((state: RootState) =>
    selectExchangeByName(state.reports, exchangeData?.name || "")
  );
  const orderData = useSelector((state: RootState) => state.reports.selectedReportOrder);
  const dispatch = useDispatch();
  const compareState = useSelector((state: RootState) => state.compare);

  // Selected metrics for all non-main tabs — used by export so column order
  // is preserved regardless of which tab is active when the user clicks Export.
  // Tabs with an empty array signal "all metrics deselected" so the backend
  // skips the sheet; tabs never touched remain absent so the backend uses defaults.
  const allTabsMetrics = useSelector((state: RootState) => {
    if (!exchangeData?.id) return null;
    const result: Record<string, string[]> = {};
    tabRegistry.forEach(tab => {
      if (tab.tabId === "main") return;
      const key = buildCacheKey(exchangeData.id, tab.tabId);
      const entry = state.tabData.entries[key];
      if (entry) {
        result[tab.tabId] = entry.selectedMetrics;
      } else if (tab.exportOnly && authUser.user_type === 'Client' && tab.tabId in tabAccess) {
        if (Array.isArray(tabAccess[tab.tabId]) && tabAccess[tab.tabId].length === 0) {
          result[tab.tabId] = [];
        }
      }
    });
    return Object.keys(result).length > 0 ? result : null;
  }, shallowEqual);

  // Loading state for the currently active non-main tab (used to disable controls contextually)
  const activeTabLoading = useSelector((state: RootState) => {
    if (activeTabId === "main" || !exchangeData?.id) return false;
    const key = buildCacheKey(exchangeData.id, activeTabId);
    return state.tabData.entries[key]?.isLoading ?? false;
  });

  // True when no tab has anything useful to export — either no data or all metrics deselected
  const allTabsEmpty = useSelector((state: RootState) => {
    // Main tab: has data AND has selected metrics
    const mainHasContent = tableData.length > 0 && selectedMetrics.length > 0;
    if (mainHasContent) return false;

    if (!exchangeData?.id) return true;

    // Check non-main tabs: need both rows and selected metrics
    return !visibleTabs.some(tab => {
      if (tab.tabId === "main") return false;
      const key = buildCacheKey(exchangeData.id, tab.tabId);
      const entry = state.tabData.entries[key];
      return entry && entry.rows.length > 0 && entry.selectedMetrics.length > 0;
    });
  });

  // Debounced data fetch for metric selections/deselections
  const debouncedFetchData = useCallback((updatedExchangeData: any) => {
    if (debounceApiTimeout.current) {
      clearTimeout(debounceApiTimeout.current);
    }

    debounceApiTimeout.current = setTimeout(() => {
      fetchSelectedExchangesData([updatedExchangeData], true, {
        page: currentPageRef.current,
        per_page: itemsPerPageRef.current,
        enable_pagination: true,
        sort_by: sortByRef.current,
        sort_order: sortOrderRef.current
      });
    }, 1500);
  }, [startDate, endDate]);

  // Constants and utility functions
  const requiredMetrics = ['line_item_name', 'start_date', 'end_date'];
  const isMobile = useIsMobile();
  const isRequiredMetric = (metric: string) => {
    return requiredMetrics.some(m => formatMetricString(m) === formatMetricString(metric));
  };

  /**
   * Check if a metric should be shown based on selected line items
   * Metrics are shown if they belong to exchanges that have at least one selected line item
   * @param {string} metric - Metric name to check
   * @returns {boolean} True if metric should be shown in dropdown
   */
  const isMetricAvailableInData = (metric: string) => {
    // Required metrics are always shown
    if (isRequiredMetric(metric)) return true;

    // If no line items selected, hide all optional metrics
    if (selectedLineItems.length === 0) return false;

    // Get all exchanges and line items from exchangeData
    const exchanges = exchangeData?.exchanges || [];
    const lineItems = exchangeData?.lineItems || [];

    // If no exchanges or line items data, show all metrics (initial load state)
    if (exchanges.length === 0 || lineItems.length === 0) return true;

    // Find which exchanges have at least one selected line item
    const selectedExchangeIds = new Set<number>();

    lineItems.forEach((lineItem: any) => {
      if (selectedLineItems.includes(lineItem.Id)) {
        selectedExchangeIds.add(lineItem.exchange);
      }
    });

    // If no exchanges have selected line items, hide metric
    if (selectedExchangeIds.size === 0) return false;

    // Collect all metrics from exchanges that have selected line items
    const availableMetrics = new Set<string>();
    exchanges.forEach((ex: any) => {
      if (selectedExchangeIds.has(ex.exchange_id)) {
        (ex.metrics || []).forEach((m: string) => {
          availableMetrics.add(formatMetricString(m));
        });
      }
    });

    // Check if this metric exists in any selected exchange (case-insensitive)
    return availableMetrics.has(formatMetricString(metric));
  };

  const tableTitle = exchangeData?.name || "";
  const lineItems = exchangeData?.lineItems || [];

  // Initialize exchange options and selections when exchange identity changes
  useEffect(() => {
    if (!exchangeData) return;
    if (initializedExchangeNameRef.current === (exchangeData.name || "")) return;
    initializeExchangeData(exchangeData);
    initializedExchangeNameRef.current = exchangeData.name || "";
  }, [exchangeData]);

  // Redux sync effect — syncs local metrics with Redux when changed externally (e.g., CustomizeMetricsModal)
  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (!reduxExchangeData) return;

    // Serialize Redux metrics for dedup
    const reduxMetricsKey = JSON.stringify(
      (reduxExchangeData.selectedMetrics || []).map((m: string) => formatMetricString(m)).sort()
    );

    // Skip if we already processed this exact Redux state
    if (reduxMetricsKey === lastReduxMetricsRef.current) return;

    const currentMetrics = selectedMetricsRef.current;
    const currentMetricsNormalized = new Set(currentMetrics.map(m => formatMetricString(m)));
    const reduxMetricsNormalized = new Set(
      reduxExchangeData.selectedMetrics?.map((m: string) => formatMetricString(m)) || []
    );

    const hasDifference =
      currentMetrics.length !== (reduxExchangeData.selectedMetrics?.length || 0) ||
      currentMetrics.some(m => !reduxMetricsNormalized.has(formatMetricString(m))) ||
      (reduxExchangeData.selectedMetrics || []).some((m: string) => !currentMetricsNormalized.has(formatMetricString(m)));

    // Record we've processed this Redux state (BEFORE any fetch, to prevent re-entry)
    lastReduxMetricsRef.current = reduxMetricsKey;

    if (hasDifference) {
      const currentMetricsList = metricsListRef.current;
      const updatedMetrics = (reduxExchangeData.selectedMetrics || []).filter((metric: string) => {
        const originalCase = currentMetricsList.find(m =>
          formatMetricString(m) === formatMetricString(metric)
        );
        return originalCase ? true : false;
      }).map((metric: string) => {
        const originalCase = currentMetricsList.find(m =>
          formatMetricString(m) === formatMetricString(metric)
        );
        return originalCase || metric;
      });

      setSelectedMetrics(updatedMetrics);

      const updatedExchangeData = {
        ...exchangeDataRef.current,
        selectedMetrics: updatedMetrics
      };
      setExchangeData(updatedExchangeData);
      fetchSelectedExchangesData([updatedExchangeData], true, {
        page: currentPageRef.current,
        per_page: itemsPerPageRef.current,
        enable_pagination: true,
        sort_by: sortByRef.current,
        sort_order: sortOrderRef.current
      });
    }
  }, [reduxExchangeData]);

  useEffect(() => {
    // fetch reports data on global data by change
    if (dataBy !== exchangeDataRef.current.dataBy) {
      setExchangeReportDataBy(dataBy);

      dispatch(setExchangeDataBy({
        exchangeName: exchangeDataRef.current?.name || "",
        dataBy: dataBy
      }));

      const updatedExchangeData = {
        ...exchangeDataRef.current,
        dataBy: dataBy
      };
      setExchangeData(updatedExchangeData);
      setCurrentPage(1); // Reset to first page on data-by change

      fetchSelectedExchangesData([updatedExchangeData], true, {
        page: 1,
        per_page: itemsPerPageRef.current,
        enable_pagination: true,
        sort_by: sortByRef.current,
        sort_order: sortOrderRef.current
      });
    }

  }, [dataBy])

  useEffect(() => {
    if (!exchangeDataRef.current) return;

    // Clear previous debounce timeout
    if (dateChangeTimeoutRef.current) {
      clearTimeout(dateChangeTimeoutRef.current);
    }

    // Debounce the API call to prevent excessive requests during rapid date changes
    dateChangeTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on date range change
      fetchSelectedExchangesData([exchangeDataRef.current], true, {
        page: 1,
        per_page: itemsPerPageRef.current,
        enable_pagination: true,
        sort_by: sortByRef.current,
        sort_order: sortOrderRef.current
      });
      isInitializedRef.current = true; // Enable Redux sync effect after initial fetch
    }, 300);

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (dateChangeTimeoutRef.current) {
        clearTimeout(dateChangeTimeoutRef.current);
      }
    };
  }, [startDate, endDate]);


  /**
   * Fetches exchange data based on current selections
   * @param {any[]} exchangesData - Array of exchange data objects
   * @param {boolean} isIndividualExchange - Flag for individual exchange fetch
   */
  const fetchSelectedExchangesData = async (exchangesData: any[], isIndividualExchange: boolean = false, paginationOptions?: { page?: number, per_page?: number, enable_pagination?: boolean, sort_by?: string, sort_order?: 'asc' | 'desc' }) => {
    if (exchangesData.length == 0) {
      return;
    }

    // Reset empty columns before fetching fresh data
    if (isIndividualExchange) {
      const exchangeName = exchangesData[0].name;

      dispatch(setExchangeEmptyColumns({
        exchangeName: exchangeName,
        emptyColumns: []
      }));

      setExchangeData((prev: any) => ({
        ...prev,
        emptyColumns: []
      }));
    }

    // Check if selectedMetrics or selectedLineItems are empty in the first exchange
    if (!exchangesData[0].selectedMetrics?.length || !exchangesData[0].selectedLineItems?.length) {
      // toast.warning(`No metrics or line items selected for the ${tableTitle}.`);
      setExchangeReportData([]);
      setLineItemLoading(false); // Reset line item loading on empty selections
      return;
    }

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      // Delay to allow HTTP connections to close properly (prevents connection pool stalling)
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    // Create new abort controller for current request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let wasCanceled = false;

    try {
      setTableLoading(true);

      const selectedExchangeData = formatSelectedExchanges(exchangesData);
      // If no selection, skip API call
      if (Object.keys(selectedExchangeData).length === 0) {
        setTableLoading(false);
        return;
      }

      const payload = {
        ...selectedExchangeData,
        page: paginationOptions?.page || currentPage,
        per_page: paginationOptions?.per_page || itemsPerPage,
        enable_pagination: paginationOptions?.enable_pagination !== undefined ? paginationOptions.enable_pagination : true,
        sort_by: paginationOptions?.sort_by || sortBy,
        sort_order: paginationOptions?.sort_order || sortOrder
      };

      const response = await axiosInstance.post('/campaign-report-data', payload, {
        headers: { 'Content-Type': 'application/json' },
        signal: abortController.signal,
      });

      // Check for empty response
      if (!response?.data) {
        toast.error("No data available for the selections.");
        setTableLoading(false);
        return;
      }

      // Handle both paginated and non-paginated response formats
      const responseData = response.data.data ? response.data.data : response.data;
      const paginationInfo = response.data.pagination || null;

      // Update pagination state from API response
      if (paginationInfo) {
        setCurrentPage(paginationInfo.current_page);
        setTotalPages(paginationInfo.total_pages);
        setTotalRecords(paginationInfo.total_records);
        setItemsPerPage(paginationInfo.per_page);
      }

      // Extract empty columns list from backend response
      let detectedEmptyColumns: string[] = [];
      if (isIndividualExchange && responseData.length > 0) {
        detectedEmptyColumns = responseData[0]?.empty_columns || [];
      }

      if (isIndividualExchange) {
        // Update existing data for individual exchange
        setExchangeReportData(prevData => {
          const newData = Array.isArray(responseData) ? responseData : [responseData];
          const updatedData = [...prevData];

          newData.forEach(newItem => {
            const existingIndex = updatedData.findIndex(
              item => item.report_name === newItem.report_name
            );

            if (existingIndex >= 0) {
              // Update existing exchange data
              updatedData[existingIndex] = newItem;
            } else {
              // Add new exchange data
              updatedData.push(newItem);
            }
          });

          return updatedData;
        });

        // Update both local and Redux state with detected empty columns
        setExchangeData((prev: any) => ({
          ...prev,
          emptyColumns: detectedEmptyColumns
        }));

        dispatch(setExchangeEmptyColumns({
          exchangeName: exchangesData[0].name,
          emptyColumns: detectedEmptyColumns
        }));
      } else {
        // For all exchanges, replace the data
        setExchangeReportData(responseData);
      }

    } catch (error) {
      // Ignore abort errors - they're intentional
      if (axios.isCancel(error)) {
        wasCanceled = true;
        return;
      }
      console.error("Error fetching data:", error);
    } finally {
      // Only update loading state if request was not canceled
      // This prevents aborted requests from clearing loading state of new requests
      if (!wasCanceled) {
        setTimeout(() => {
          setTableLoading(false);
          setLineItemLoading(false); // Always reset line item loading when API call completes
        }, 200); // Reduced to 200ms for better perceived performance
      }
    }
  };

  /**
   * Formats exchange data for API request payload
   * @param {any[]} selectedExchanges - Array of selected exchanges
   * @returns {Object} Formatted payload for API request
   */
  const formatSelectedExchanges = (selectedExchanges: any[]) => {

    if (startDate === "" || endDate === "") return {};

    const databy = selectedExchanges[0].dataBy || dataBy; // Default reporting period
    const formattedData = selectedExchanges.map(exchange => {
      const selectedMetrics = exchange.selectedMetrics || [];
      const selectedLineItems = exchange.selectedLineItems || [];

      // Return empty object if no metrics or line items selected
      if (selectedMetrics.length === 0 || selectedLineItems.length === 0) {
        return {};
      }

      // Validate selectedMetrics against display_metrics using formatMetricString for comparison
      const displayMetrics = exchange.display_metrics || [];
      const displayMetricsNormalized = new Set(
        displayMetrics.map((metric: string) => formatMetricString(metric))
      );

      // Filter selectedMetrics to only include metrics that exist in display_metrics
      const validSelectedMetrics = selectedMetrics.filter((metric: string) =>
        displayMetricsNormalized.has(formatMetricString(metric))
      );

      const filteredExchanges = (exchange.exchanges || []).map((ex: any) => {
        // Create normalized set for comparison (preserves original case in output)
        const validSelectedMetricsNormalized = new Set(
          validSelectedMetrics.map((m: string) => formatMetricString(m))
        );

        // Match metrics in the order of validSelectedMetrics (preserving original case from exchange)
        const matchedMetrics = validSelectedMetrics
          .map((selectedMetric: string) => {
            // Find the matching metric in ex.metrics (preserving original case)
            return (ex.metrics || []).find((exMetric: string) =>
              formatMetricString(exMetric) === formatMetricString(selectedMetric)
            );
          })
          .filter((metric: any): metric is string => metric !== undefined);

        // If no metrics are selected, return an empty object for this exchange
        const exchangeLineItems = (exchange.lineItems || [])
          .filter((item: any) =>
            item.exchange === ex.exchange_id &&
            selectedLineItems.includes(item.Id)
          )
          .map((item: any) => ({
            id: item.Id,
            name: item.Line_Item_Name__c
          }));

        return {
          exchange_id: ex.exchange_id,
          exchange_table: ex.exchange_table,
          metrics: matchedMetrics,
          line_items: exchangeLineItems
        };
      }).filter((ex: { line_items: string | any[]; }) => ex.line_items && ex.line_items.length > 0);

      return {
        name: exchange.name,
        selected_metrics: validSelectedMetrics,
        exchanges: filteredExchanges,
      };
    });

    return {
      order_id: selectedOrder,
      start_date: startDate,
      end_date: endDate,
      data_by: databy,
      selected_reports: formattedData,
    };
  };

  /**
   * Initializes exchange data with line items and metrics
   * @param {any} exchangeData - Exchange data to initialize
   */
  const initializeExchangeData = (exchangeData: any) => {

    // Set exchange Report data by
    if (exchangeData.dataBy) {
      setExchangeReportDataBy(exchangeData.dataBy);
    }

    // Set line items
    if (exchangeData.lineItems) {
      setLineItemsOptions(exchangeData.lineItems);
    }

    // Set selected line items
    if (exchangeData.selectedLineItems) {
      setSelectedLineItems(exchangeData.selectedLineItems);
    }

    // Set metrics
    if (exchangeData.display_metrics) {
      const parsedUdsMetrics = JSON.parse(userState.user.user.uds_metrics || '[]');

      const filteredOptionalMetrics = exchangeData.display_metrics
        .filter((metric: string) => {
          if (!isRequiredMetric(metric)) {
            if (userState.user.user.user_type === 'Client') {
              const userSelectedMetrics = getUserSelectedMetrics(exchangeData.id, parsedUdsMetrics);
              return userSelectedMetrics.some(savedMetric =>
                formatMetricString(savedMetric) === formatMetricString(metric)
              );
            }
            return true;
          }
          return false;
        });

      // Then, add the required metrics at the beginning
      const optionalMetrics = [
        ...requiredMetrics.filter(metric =>
          // Only include required metrics that exist in display_metrics
          exchangeData.display_metrics.includes(metric)
        ),
        ...filteredOptionalMetrics
      ];
      setMetricsList(optionalMetrics);
      const initialSelected = exchangeData.selectedMetrics || [];
      setSelectedMetrics(initialSelected);
    }
  };

  // Transform report data into flat table data
  useEffect(() => {
    const flatData: TableDataItem[] = [];
    const allMetrics = new Set<string>();

    if (exchangeReportData.length === 0) {
      setTableData([]);
      // Dispatch no data status to Redux
      dispatch(setExchangeHasData({
        exchangeName: tableTitle,
        hasData: false
      }));
      return
    };

    const orderedData = exchangeReportData[0]?.ordered_data || [];

    // Process ordered_data (new flat structure)
    orderedData.forEach((row: any) => {
      const { line_item_id, group_col, ...rest } = row;

      // Create normalized row with all data
      const normalizedRow: TableDataItem = { line_item_name: rest.line_item_name || '' };

      Object.entries(rest).forEach(([key, value]) => {
        normalizedRow[key] = value;
        allMetrics.add(key);
      });

      flatData.push(normalizedRow);
    });

    // Data is already sorted by the backend based on sortBy and sortOrder
    // No client-side sorting needed
    setTableData(flatData);

    // Dispatch data availability status to Redux
    dispatch(setExchangeHasData({
      exchangeName: tableTitle,
      hasData: flatData.length > 0
    }));
  }, [exchangeReportData, tableTitle, dispatch]);

  // Set up column order when table data changes
  useEffect(() => {
    if (tableData.length > 0) {
      const allColumns = Array.from(new Set(tableData.flatMap(item => Object.keys(item))));
      const uniqueColumns = getUniqueCaseInsensitiveMetrics(allColumns);

      // Build required metrics in correct order
      const requiredMetricsInOrder = requiredMetrics.filter(metric =>
        uniqueColumns.some(col => formatMetricString(col) === formatMetricString(metric))
      );

      // Get other non-required metrics
      const otherMetrics = uniqueColumns.filter(metric =>
        !isRequiredMetric(metric)
      );

      // New order with required first, then selectedMetrics in their exact order
      const initialOrder = [
        ...requiredMetricsInOrder,
        ...selectedMetrics.filter(metric =>
          uniqueColumns.some(col => formatMetricString(col) === formatMetricString(metric))
        )
      ];

      // Add any missing metrics (not in selectedMetrics)
      const missingMetrics = otherMetrics.filter(metric =>
        !initialOrder.some(m => formatMetricString(m) === formatMetricString(metric))
      );

      if (missingMetrics.length > 0) {
        initialOrder.push(...missingMetrics);
      }

      // Update columnOrder if it's different from current
      const orderChanged = columnOrder.length !== initialOrder.length ||
        columnOrder.some((col, idx) => formatMetricString(col) !== formatMetricString(initialOrder[idx]));

      if (columnOrder.length === 0 || orderChanged) {
        setColumnOrder(initialOrder);
        setTableColumnOrder(initialOrder);
      }
    }
  }, [tableData, selectedMetrics]);

  /**
   * Handles metrics selection changes
   * @param {string[]} newMetrics - Array of selected metric names
   * @param {boolean} skipRefetch - Skip API refetch (used by bulk operations like Select All/Default Columns)
   */
  const handleMetricsChange = (newMetrics: string[], skipRefetch: boolean = false) => {
    const uniqueNewMetrics = getUniqueCaseInsensitiveMetrics(newMetrics);

    // Create order with required first, then selectedMetrics in their order
    const finalOrder = [
      ...requiredMetrics,
      ...uniqueNewMetrics.filter(metric => !isRequiredMetric(metric))
    ];

    setSelectedMetrics(uniqueNewMetrics);
    setColumnOrder(finalOrder);
    setTableColumnOrder(finalOrder);

    if (exchangeData) {
      const updatedExchangeData = {
        ...exchangeData,
        selectedMetrics: uniqueNewMetrics
      };
      setExchangeData(updatedExchangeData);

      // Only fetch data if not skipping refetch
      if (!skipRefetch) {
        setCurrentPage(1); // Reset to first page on metrics change
        fetchSelectedExchangesData([updatedExchangeData], true, {
          page: 1,
          per_page: itemsPerPage,
          enable_pagination: true,
          sort_by: sortBy,
          sort_order: sortOrder
        });
      }

      dispatch(setSelectedExchangeMetrics({
        exchangeName: tableTitle,
        metrics: uniqueNewMetrics
      }));
      lastReduxMetricsRef.current = JSON.stringify(
        uniqueNewMetrics.map(m => formatMetricString(m)).sort()
      );
    }
  };

  /**
 * Gets sorted list of metrics for display
 * @returns {string[]} Sorted array of metric names
 */
  const getSortedMetricsList = () => {
    const defaultMetrics = exchangeData?.defaultMetrics
      ? getUniqueCaseInsensitiveMetrics(exchangeData.defaultMetrics)
      : [];

    // Use the current columnOrder as the base for ordering
    // This preserves the drag and drop order
    // Get all non-required metrics from columnOrder
    const orderedMetrics = columnOrder
      .filter(metric => !isRequiredMetric(metric))
      .map(m => m);

    // Get metrics that are in the full list but not in our current order
    const missingMetrics = metricsList.filter(
      metric => !orderedMetrics.some(m =>
        m && metric && formatMetricString(m) === formatMetricString(metric)
      )
    );

    // Combine ordered metrics with any missing ones
    const allMetrics = [...orderedMetrics, ...missingMetrics];

    // Remove duplicates while preserving order
    const uniqueMetrics = Array.from(
      new Map(
        allMetrics
          .filter(metric => metric != null)
          .map(metric => [formatMetricString(metric), metric])
      ).values()
    );

    // On initial load (before user interaction), preserve backend order for selectedMetrics
    if (!hasUserInteracted) {
      const selectedNonEmpty: string[] = [];
      const unselectedNonEmpty: string[] = [];
      const allEmpty: string[] = [];

      // Separate selected metrics into non-empty and empty
      selectedMetrics.forEach(selectedMetric => {
        const matchingMetric = uniqueMetrics.find(
          metric => formatMetricString(metric) === formatMetricString(selectedMetric)
        );
        if (matchingMetric) {
          const formattedMetric = formatMetricString(matchingMetric);
          const isEmpty = allEmptyColumns.has(formattedMetric);

          if (isEmpty) {
            allEmpty.push(matchingMetric);
          } else {
            selectedNonEmpty.push(matchingMetric);
          }
        }
      });

      // Add unselected non-empty metrics
      const allEmptySet = new Set(allEmpty.map(m => formatMetricString(m)));

      uniqueMetrics.forEach(metric => {
        const isSelected = selectedMetrics.some(
          m => formatMetricString(m) === formatMetricString(metric)
        );
        const formattedMetric = formatMetricString(metric);
        const isEmpty = allEmptyColumns.has(formattedMetric);

        if (!isSelected && !isEmpty) {
          unselectedNonEmpty.push(metric);
        } else if (!isSelected && isEmpty && !allEmptySet.has(formattedMetric)) {
          allEmpty.push(metric);
        }
      });

      // Return: selected non-empty -> unselected non-empty -> all empty at bottom
      return [...selectedNonEmpty, ...unselectedNonEmpty, ...allEmpty];
    }

    // After user interaction, use original sorting behavior
    return uniqueMetrics.sort((a, b) => {
      // Check if metrics are empty
      const aEmpty = allEmptyColumns.has(formatMetricString(a));
      const bEmpty = allEmptyColumns.has(formatMetricString(b));

      // Check if metrics are selected (and not empty)
      const aSelected = !aEmpty && selectedMetrics.some(m =>
        m && a && formatMetricString(m) === formatMetricString(a)
      );
      const bSelected = !bEmpty && selectedMetrics.some(m =>
        m && b && formatMetricString(m) === formatMetricString(b)
      );

      // Empty metrics always go to bottom
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (aEmpty && bEmpty) {
        // Both empty, preserve order
        return 0;
      }

      // Among non-empty: selected first, then unselected
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // For metrics with the same selection status, preserve the columnOrder
      const aIndex = orderedMetrics.findIndex(m =>
        m && a && formatMetricString(m) === formatMetricString(a)
      );
      const bIndex = orderedMetrics.findIndex(m =>
        m && b && formatMetricString(m) === formatMetricString(b)
      );

      // If both are in columnOrder, preserve that order
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;

      // If only one is in columnOrder, put it first
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // If neither is in columnOrder, maintain their current order
      return 0;
    });
  };

  /**
   * Toggles metric selection
   * @param {string} metric - Metric name to toggle
   */
  const handleMetricToggle = (metric: string) => {
    setHasUserInteracted(true);

    const caseVariations = metricsList.filter(m =>
      formatMetricString(m) === formatMetricString(metric)
    );

    // Detect if this is a deselection operation
    const isDeselecting = selectedMetrics.some(m =>
      caseVariations.some(v => formatMetricString(v) === formatMetricString(m))
    );

    let newMetrics;
    if (isDeselecting) {
      newMetrics = selectedMetrics.filter(m =>
        !caseVariations.some(v => formatMetricString(v) === formatMetricString(m))
      );
    } else {
      newMetrics = [...selectedMetrics, metric];
    }

    // Update UI state immediately for both selection and deselection
    const uniqueNewMetrics = getUniqueCaseInsensitiveMetrics(newMetrics);

    // Create column order with required first, then selectedMetrics in their order
    const finalOrder = [
      ...requiredMetrics,
      ...uniqueNewMetrics.filter(metric => !isRequiredMetric(metric))
    ];

    // Update states immediately for responsive UI
    setSelectedMetrics(uniqueNewMetrics);
    setColumnOrder(finalOrder);
    setTableColumnOrder(finalOrder);

    if (exchangeData) {
      const updatedExchangeData = {
        ...exchangeData,
        selectedMetrics: uniqueNewMetrics
      };
      setExchangeData(updatedExchangeData);

      // Only call API on metric selection (adding new metrics)
      // Deselection just hides columns in frontend - data already exists
      if (!isDeselecting) {
        // Use debounced API call for selections
        // This will accumulate all changes during the 1.5s window and fetch once with final state
        debouncedFetchData(updatedExchangeData);
      }

      // Redux dispatch remains immediate for both selection and deselection
      dispatch(setSelectedExchangeMetrics({
        exchangeName: tableTitle,
        metrics: uniqueNewMetrics
      }));
      lastReduxMetricsRef.current = JSON.stringify(
        uniqueNewMetrics.map(m => formatMetricString(m)).sort()
      );
    }
  };

  /**
   * Handles select all/none for metrics
   * @param {boolean} checked - Whether to select all or none
   */
  const handleSelectAll = (checked: boolean) => {
    setHasUserInteracted(true);

    if (debounceApiTimeout.current) {
      clearTimeout(debounceApiTimeout.current);
      debounceApiTimeout.current = null;
    }

    if (checked) {
      handleMetricsChange([...metricsList]);
    } else {
      handleMetricsChange([]);
    }
  };

  /**
   * Handles default columns selection
   * @param {boolean} checked - Whether to select default columns
   */
  const handleDefaultColumns = (checked: boolean) => {
    setHasUserInteracted(true);

    if (debounceApiTimeout.current) {
      clearTimeout(debounceApiTimeout.current);
      debounceApiTimeout.current = null;
    }

    const defaultColumns = exchangeData?.defaultMetrics
      ? getUniqueCaseInsensitiveMetrics(exchangeData.defaultMetrics)
      : [];
    if (checked) {
      handleMetricsChange([...defaultColumns]);
    } else {
      const newMetrics = selectedMetrics.filter(m =>
        !defaultColumns.some(d => formatMetricString(d) === formatMetricString(m))
      );
      handleMetricsChange(newMetrics);
    }
  };

  // utility function to format cell data based on column
  const formatColumnValue = (columnName: string, value: any) => {
    if (value === null || value === undefined) return "-";

    // Check for empty or whitespace-only strings
    if (typeof value === "string" && value.trim() === "") return "-";

    const formattedColumnName = formatMetricString(columnName);

    if (formattedColumnName === formatMetricString("amount_type")) {
      return value.toString().toUpperCase();
    }

    // Add other column-specific formatting rules here
    // if (lowerColumnName === "other_column") {
    //   return specificFormatting(value);
    // }

    return value;
  };

  /**
   * Calculates dynamic column width based on content length
   * @param {string} actualKey - The column key
   * @returns {object} Column sizing configuration
   */
  const calculateDynamicColumnWidth = (actualKey: string) => {
    const headerLength = formatMetricName(actualKey).length;

    // Find the maximum content length for this column
    const maxContentLength = tableData.reduce((max, row) => {
      const rowKey = Object.keys(row).find(
        k => formatMetricString(k) === formatMetricString(actualKey)
      ) || actualKey;
      const cellValue = row[rowKey];

      let displayValue = '';
      if (typeof cellValue === "string" && cellValue.includes("_")) {
        displayValue = formatMetricName(cellValue);
      } else {
        displayValue = formatColumnValue(actualKey, cellValue)?.toString() || '';
      }

      return Math.max(max, displayValue.length);
    }, headerLength);

    // Calculate width: ~8px per character + padding
    // Minimum 80px, maximum 400px to prevent extreme sizes
    const calculatedWidth = Math.min(Math.max(maxContentLength * 8 + 32, 80), 400);

    return {
      minWidth: 150,
      width: 150,
      maxWidth: calculatedWidth + 50 // Allow some resize flexibility
    };
  };

  // Memoized check for all default columns selected
  const areAllDefaultColumnsSelected = useMemo(() => {
    const defaultColumns = exchangeData?.defaultMetrics
      ? getUniqueCaseInsensitiveMetrics(exchangeData.defaultMetrics)
      : [];

    // Check if selection exactly matches default metrics (no more, no less)
    if (defaultColumns.length === 0) return false;
    if (selectedMetrics.length !== defaultColumns.length) return false;

    return defaultColumns.every((defaultMetric: string) =>
      selectedMetrics.some(selectedMetric =>
        formatMetricString(selectedMetric) === formatMetricString(defaultMetric)
      )
    );
  }, [selectedMetrics, exchangeData]);

  // Memoized check for all metrics selected
  const areAllMetricsSelected = useMemo(() => {
    return selectedMetrics.length === metricsList.length;
  }, [selectedMetrics, metricsList]);

  // Track which columns have no data to hide them from table
  const allEmptyColumns = useMemo(() => {
    const emptyColumns = exchangeData?.emptyColumns || [];
    return new Set(emptyColumns.map((col: string) => formatMetricString(col)));
  }, [exchangeData?.emptyColumns]);

  // Memoized table columns configuration
  const columns = useMemo(() => {
    if (!tableData.length) return [];

    // First create a map of all available keys from data (case-insensitive)
    const availableKeys = new Map<string, string>();
    tableData.forEach(row => {
      Object.keys(row).forEach(key => {
        const formattedKey = formatMetricString(key);
        if (!availableKeys.has(formattedKey)) {
          availableKeys.set(formattedKey, key); // Store original case
        }
      });
    });

    // Get the sorted metrics list (same order as dropdown - selected first)
    const sortedMetrics = getSortedMetricsList();

    // Add required metrics at the beginning
    const requiredInData = requiredMetrics.filter(m =>
      availableKeys.has(formatMetricString(m))
    );

    // Combine required + sorted metrics
    const orderedColumns = [...requiredInData, ...sortedMetrics];

    // Create columns in dropdown order sequence (selected metrics first)
    return orderedColumns
      .filter(key => {
        // Only show columns that exist in tableData
        if (!availableKeys.has(formatMetricString(key))) return false;

        // If no metrics selected at all, show nothing
        if (selectedMetrics.length === 0) return false;

        // Always show required metrics when at least one metric is selected
        if (isRequiredMetric(key)) return true;

        // Filter out columns that only contain empty values
        if (allEmptyColumns.has(formatMetricString(key))) return false;

        // Show only if metric is in selectedMetrics
        return selectedMetrics.some(m => formatMetricString(m) === formatMetricString(key));
      })
      .map((key) => {
        // Get the original case version of the key
        const actualKey = availableKeys.get(formatMetricString(key)) || key;

        const baseColumn = {
          Header: formatMetricName(actualKey),
          accessor: (row: any) => {
            // Find the actual key in the row (case-insensitive)
            const rowKey = Object.keys(row).find(
              k => formatMetricString(k) === formatMetricString(actualKey)
            ) || actualKey;
            return row[rowKey];
          },
          id: actualKey, // Important to set explicit id
          disableSortBy: false,
          Cell: ({ value }: { value: any }) => {
            if (formatMetricString(actualKey) === formatMetricString("line_item_name")) {
              return (
                <div className="line-item-ellipsis" title={value ?? "-"}>
                  {value ?? "-"}
                </div>
              );
            }

            if (typeof value === "string" && value.includes("_")) {
              return (
                <div className="ellipsis-cell" title={formatMetricName(value)}>
                  {formatMetricName(value)}
                </div>
              );
            }

            if (typeof value === "string" && value.includes("%")) {
              const numericValue = parseFloat(value.replace("%", "").trim());
              if (!isNaN(numericValue)) {
                const color = numericValue > 100 ? "#31d325" : "#a7ce18ff"; //uncomment if want to add colors for %
                return (
                  <div className="ellipsis-cell" title={value}>
                    <span style={{ color }}>{value}</span>
                    {/* <span >{value}</span> */}
                  </div>
                );
              }
            }
            const formattedValue = formatColumnValue(actualKey, value);

            // Keep your existing UI structure but use formattedValue
            return (
              <div className="ellipsis-cell" title={formattedValue}>
                {formattedValue}
              </div>
            );
          },
          minWidth: 100,
          width: 100,
          maxWidth: 150,
        };

        // Special column sizing for specific fields
        if (formatMetricString(actualKey) === formatMetricString("line_item_name")) {
          return { ...baseColumn, minWidth: 150, width: 150, maxWidth: 400 };
        } else if (formatMetricString(actualKey) === formatMetricString("start_date") || formatMetricString(actualKey) === formatMetricString("end_date")) {
          return { ...baseColumn, minWidth: 120, width: 120, maxWidth: 120 };
        } else {
          // Dynamic width calculation based on content (commented for now)
          const dynamicSizing = calculateDynamicColumnWidth(actualKey);
          return { ...baseColumn, ...dynamicSizing };

          // Fixed width implementation
          // return { ...baseColumn, minWidth: 150, width: 150, maxWidth: 300 };
        }
      });
  }, [tableData, columnOrder]);

  // React Table hooks and configuration (without client-side pagination and sorting)
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    // allColumns,
    setColumnOrder: setTableColumnOrder,
  } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        columnOrder,
      },
      disableSortBy: true, // Disable client-side sorting
      manualSortBy: true,  // Enable manual sorting for server-side
    },
    useFlexLayout,
    useResizeColumns,
    useColumnOrder,
    useSortBy
  );


  /**
   * Handles column header click for server-side sorting
   * Only line_item_name, start_date, and end_date are sortable
   * @param {string} columnId - The column ID to sort by
   */
  const handleSort = (columnId: string) => {
    // Only allow sorting for specific columns
    const sortableColumns = ['line_item_name', 'start_date', 'end_date'];
    const normalizedColumnId = formatMetricString(columnId);

    const isSortable = sortableColumns.some(col =>
      formatMetricString(col) === normalizedColumnId
    );

    if (!isSortable) {
      // Column is not sortable, do nothing
      return;
    }

    let newSortOrder: 'asc' | 'desc' = 'asc';

    // If clicking the same column, toggle the order
    if (sortBy === columnId) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, default to ascending
      newSortOrder = 'asc';
    }

    setSortBy(columnId);
    setSortOrder(newSortOrder);
    setCurrentPage(1); // Reset to first page when sorting changes

    if (exchangeData) {
      fetchSelectedExchangesData([exchangeData], true, {
        page: 1,
        per_page: itemsPerPage,
        enable_pagination: true,
        sort_by: columnId,
        sort_order: newSortOrder
      });
    }
  };

  /**
   * Handles column reordering via drag and drop
   * @param {any} event - Drag end event
   */
  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    // Additional safety check: prevent dragging if active item is not selected
    const isActiveSelected = selectedMetrics.some(m =>
      formatMetricString(m) === formatMetricString(active.id)
    );

    if (!over || active.id === over.id || !isActiveSelected) return;

    setHasUserInteracted(true);

    setColumnOrder(prevOrder => {
      // Filter out any undefined or null values from the order array
      const cleanPrevOrder = prevOrder.filter(item => item != null);
      const oldIndex = cleanPrevOrder.indexOf(active.id as string);
      const newIndex = cleanPrevOrder.indexOf(over.id as string);

      // Check if indices are valid
      if (oldIndex === -1 || newIndex === -1) return prevOrder;

      const newOrder = arrayMove(cleanPrevOrder, oldIndex, newIndex);

      // Keep required metrics first
      const requiredInOrder = requiredMetrics.filter(m =>
        newOrder.some((o: string) => formatMetricString(o) === formatMetricString(m))
      );
      const otherMetrics = newOrder.filter((m: string) =>
        !requiredMetrics.some(r => formatMetricString(r) === formatMetricString(m))
      );
      const finalOrder = [...requiredInOrder, ...otherMetrics];

      setTableColumnOrder(finalOrder);

      // Update selectedMetrics to reflect the new order (excluding required metrics)
      const reorderedSelectedMetrics = finalOrder.filter(
        metric => selectedMetrics.some(m => formatMetricString(m) === formatMetricString(metric)) &&
          !isRequiredMetric(metric)
      );
      setSelectedMetrics(reorderedSelectedMetrics);

      // Update exchangeData with new metrics order
      if (exchangeData) {
        const updatedExchangeData = {
          ...exchangeData,
          selectedMetrics: reorderedSelectedMetrics
        };
        setExchangeData(updatedExchangeData);

        // Dispatch to Redux to persist the new order
        dispatch(setSelectedExchangeMetrics({
          exchangeName: tableTitle,
          metrics: reorderedSelectedMetrics
        }));
        lastReduxMetricsRef.current = JSON.stringify(
          reorderedSelectedMetrics.map(m => formatMetricString(m)).sort()
        );
      }

      return finalOrder;

    });
  };

  /**
   * SortableColumnItem Component - Draggable metric item in dropdown
   * @param {SortableColumnItemProps} props - Component props
   */
  const SortableColumnItem = ({ metric, isChecked }: SortableColumnItemProps) => {
    const isAllEmpty = allEmptyColumns.has(formatMetricString(metric));

    // Disable dragging for empty or unselected metrics
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: metric,
      disabled: !isChecked || isAllEmpty,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: isChecked ? (isAllEmpty ? 'not-allowed' : 'grab') : 'default',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      opacity: isAllEmpty ? 0.6 : (isChecked ? 1 : 0.7),
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...(isChecked && !isAllEmpty ? { ...attributes, ...listeners } : {})}
        onClick={(e) => e.stopPropagation()}
        className={isAllEmpty ? 'all-empty-metric' : ''}
        title={isAllEmpty ? "No data available for current selection" : ""}
      >
        <img src="/assets/icons/drag.svg" alt="Drag Icon" className="drag-handle" />
        <label className="checkbox-label custom-label">
          <input
            className="form-check-input checkbox-input"
            type="checkbox"
            checked={isChecked && !isAllEmpty}
            onChange={() => handleMetricToggle(metric)}
            onClick={(e) => e.stopPropagation()}
            disabled={tableLoading || isAllEmpty}
          />
          <span className="custom-checkbox-box"></span>
          <span className="metric-name-wrapper">
            {formatMetricName(metric)}
            {isAllEmpty && <span className="empty-indicator">N/A</span>}
          </span>
        </label>
      </div>
    );
  };

  // Drag and drop sensors configuration
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const sensors = useSensors(pointerSensor);

  // Click outside dropdown handler
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        !event.target.closest('.customize-columns-btn')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate dropdown max height based on available viewport space
  const calculateDropdownHeight = () => {
    if (customizeButtonRef.current) {
      const buttonRect = customizeButtonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const padding = 20; // Extra padding from viewport bottom
      const desiredHeight = 400; // Desired dropdown height

      // Only reduce height if space is really limited (less than 500px)
      if (spaceBelow - padding < 500) {
        const maxHeight = Math.max(250, spaceBelow - padding);
        setDropdownMaxHeight(maxHeight);
      } else {
        // Plenty of space (500px or more), use full desired height
        setDropdownMaxHeight(desiredHeight);
      }
    }
  };

  // Type guard function
  const isValidDataBy = (value: string): value is 'day' | 'week' | 'month' | 'quarter' | 'year' => {
    return ['day', 'week', 'month', 'quarter', 'year'].includes(value);
  };

  /**
   * Handles report data by period change
   * @param {OptionType | null} selected - Selected period option
   */
  const handleDataByChange = (selected: OptionType | null) => {
    if (selected && isValidDataBy(selected.value)) {
      setExchangeReportDataBy(selected.value);
      setCurrentPage(1); // Reset to first page on data by change

      // Update per-exchange dataBy in Redux
      dispatch(setExchangeDataBy({
        exchangeName: tableTitle,
        dataBy: selected.value
      }));

      const updatedExchangeData = {
        ...exchangeData,
        dataBy: selected.value
      };
      setExchangeData(updatedExchangeData);
      fetchSelectedExchangesData([updatedExchangeData], true, {
        page: 1,
        per_page: itemsPerPage,
        enable_pagination: true,
        sort_by: sortBy,
        sort_order: sortOrder
      });
    }
  };

  /**
   * Handles line item selection changes
   * @param {any} itemId - ID of the line item to toggle
   */
  const handleLineItemsChange = (itemId: any) => {
    // Prevent new selections if already processing
    if (lineItemLoading) return;

    setCurrentPage(1); // Reset to first page on line item change
    setLineItemLoading(true); // Disable checkboxes during operation

    setSelectedLineItems((prevSelected) => {
      const updatedSelectedItems = prevSelected.includes(itemId)
        ? prevSelected.filter((id) => id !== itemId)
        : [...prevSelected, itemId];

      if (exchangeData) {
        const updatedExchangeData = {
          ...exchangeData,
          selectedLineItems: updatedSelectedItems,
        };
        setExchangeData(updatedExchangeData);

        // Use debounced fetch to prevent multiple API calls on rapid clicks
        if (debounceApiTimeout.current) {
          clearTimeout(debounceApiTimeout.current);
        }
        debounceApiTimeout.current = setTimeout(async () => {
          try {
            await fetchSelectedExchangesData([updatedExchangeData], true, {
              page: 1,
              per_page: itemsPerPage,
              enable_pagination: true,
              sort_by: sortBy,
              sort_order: sortOrder
            });
          } finally {
            setLineItemLoading(false); // Re-enable checkboxes after operation
          }
        }, 500); // 500ms delay

        dispatch(
          setSelectedExchangeLineItems({
            exchangeName: tableTitle,
            lineItems: updatedSelectedItems,
          })
        );
      }

      return updatedSelectedItems;
    });
  };

  /**
 * Handles all line item selection changes
 * @param [] itemId - ID of the line item to toggle
 */
  const handleSelectAllChange = (allItemIds: any[]) => {
    // Prevent new selections if already processing
    if (lineItemLoading) return;

    setCurrentPage(1); // Reset to first page on select all change
    setLineItemLoading(true); // Disable checkboxes during operation
    setSelectedLineItems(allItemIds);

    if (exchangeData) {
      const updatedExchangeData = {
        ...exchangeData,
        selectedLineItems: allItemIds,
      };
      setExchangeData(updatedExchangeData);

      // Select all should be immediate - no debounce needed
      fetchSelectedExchangesData([updatedExchangeData], true, {
        page: 1,
        per_page: itemsPerPage,
        enable_pagination: true,
        sort_by: sortBy,
        sort_order: sortOrder
      }).finally(() => {
        // Ensures loading state is reset even if fetchSelectedExchangesData doesn't handle it
        setLineItemLoading(false);
      });

      dispatch(
        setSelectedExchangeLineItems({
          exchangeName: tableTitle,
          lineItems: allItemIds,
        })
      );
    }

  };

  // Export loading state
  const [loading, setLoading] = useState(false);

  /**
   * Handles export to Excel functionality
   * @param {any} aiSummary - Whether to include AI summary
   * @param {any} exchangeReportDataBy - Reporting period
   */
  const handleExportClick = async (aiSummary: any, exchangeReportDataBy: any) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post("/excel-export",
        {
          start_date: filters.startDate,
          end_date: filters.endDate,
          orderId: filters.selectedOrder,
          exchange_id: exchangeData?.id,
          data_by: exchangeReportDataBy,
          exchangeData: exchangeData,
          metrics: columnOrder,
          aiSummary: aiSummary,
          sort_by: sortBy,
          sort_order: sortOrder,
          // Per-tab selected metrics: backend uses this to order columns in tab-specific sheets
          tab_metrics: allTabsMetrics,
        },
        { responseType: 'blob' }
      );

      // Handle JSON response error case
      const contentType = response.headers["content-type"];
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const jsonData = JSON.parse(text);
        if (!jsonData.flag) {
          toast.error("No data available for export");
          setLoading(false);
          return;
        }
      }

      // Create download link for Excel file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", orderData?.name + ".xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Report generated successfully!");
      setLoading(false);
    } catch (error) {
      toast.error('Something went wrong!');
      setLoading(false);
    }
  };

  /**
   * Handle the user metrics for the selected.
   * @param {number} exchangeId
   * @param {any} udsMetrics
  */
  const getUserSelectedMetrics = (exchangeId: number, udsMetrics: any[]): string[] => {
    const exchangeMetrics = udsMetrics.find((item: any) => item.exchangeId === exchangeId);
    return exchangeMetrics?.selectedMetrics || [];
  };

  const topRef = useRef<HTMLDivElement>(null);

  // Server-side pagination handlers
  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;

    if (exchangeData) {
      fetchSelectedExchangesData([exchangeData], true, {
        page: newPage,
        per_page: itemsPerPage,
        enable_pagination: true,
        sort_by: sortBy,
        sort_order: sortOrder
      });
    }

    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setItemsPerPage(newPageSize);

    if (exchangeData) {
      fetchSelectedExchangesData([exchangeData], true, {
        page: 1,
        per_page: newPageSize,
        enable_pagination: true,
        sort_by: sortBy,
        sort_order: sortOrder
      });
    }
  };

  const handleCompareClick = () => {
    const lineItemIds = (exchangeData?.lineItems || []).map((item: any) => String(item.Id));
    const currentOrder = (orders ?? []).find((o: any) => o.Id === selectedOrder);
    dispatch(openPanel({
      exchangeGroupName: tableTitle,
      lineItemIds,
      orderStart: currentOrder?.EffectiveDate ?? startDate,
      orderEnd:   currentOrder?.EndDate        ?? endDate,
    }));
  };

  // Main component render
  return (
    <>
    <div className="exchange-card card" style={{ marginBottom: noOfExchanges == 1 ? "150px" : "20px" }} >
      <div className="table-header">
        <div className="exchange-name">
          {exchangeData?.iconUrl && (
            <div className="custom-icon-box">
              <img
                src={exchangeData?.iconUrl}
                alt={`${tableTitle} icon`}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
                width="auto"
                height="17px"
              />
            </div>
          )}
          <h3>{tableTitle}</h3>
        </div>
        <div className="right-side">
          <label className="checkbox-label custom-label inline-nowrap-auto-width">
            <input
              type="checkbox"
              className="custom-checkbox"
              checked={includeAISummary}
              onChange={(e) => setIncludeAISummary(e.target.checked)}
            />
            Include AI Summary
          </label>
          <div>
            <DataByDropdown
              selectedOption={exchangeReportDataBy}
              onChange={handleDataByChange}
            />
          </div>
          <LineItemDropdown
            lineItems={lineItemsOptions}
            selectedItems={selectedLineItems}
            handleCheckboxChange={handleLineItemsChange}
            handleSelectAllChange={handleSelectAllChange}
            module="report-page"
            disabled={lineItemLoading}
          />
          {activeTabId !== "main" ? (
            <CustomizeColumns
              tabId={activeTabId}
              exchangeId={exchangeData?.id}
              isLoading={activeTabLoading}
              metricLabels={visibleTabs.find(t => t.tabId === activeTabId)?.metricLabels}
            />
          ) : (
          <div className="columns-dropdown-container">
            <button
              ref={customizeButtonRef}
              className="btn btn-outline-primary button-secondary customize-columns-btn"
              color="light"
              onClick={() => {
                calculateDropdownHeight();
                setDropdownOpen(prev => !prev);
              }}
            >
              Customize Columns
            </button>
            <div
              className={`customize-columns-dropdown custom-dropdown-list ${dropdownOpen ? 'open' : ''}`}
              ref={dropdownRef}
              style={{ maxHeight: `${dropdownMaxHeight}px` }}
            >
              <div className="columns-selection">
                <label className="checkbox-label custom-label inline-nowrap">
                  <input
                    className="form-check-input checkbox-input"
                    type="checkbox"
                    checked={areAllDefaultColumnsSelected && !areAllMetricsSelected}
                    onChange={(e) => handleDefaultColumns(e.target.checked)}
                    disabled={tableLoading || exchangeData?.defaultMetrics?.length === 0}
                  />
                  Default Columns
                </label>
                <div className="vertical-line-divider"></div>
                <label className="checkbox-label custom-label inline-nowrap">
                  <input
                    className="form-check-input checkbox-input"
                    type="checkbox"
                    checked={selectedMetrics.length === metricsList.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={tableLoading}
                  />
                  Select All
                </label>
              </div>
              <Input
                type="search"
                placeholder="Search metrics"
                value={searchTerm}
                onChange={handleSearchChange}
                className="column-search-input"
                disabled={false}
              />
              <div className="columns-list custom-scrollbar">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={selectedMetrics}
                    strategy={verticalListSortingStrategy}
                  >
                    {getSortedMetricsList()
                      .filter((metric: string) => {
                        // Filter out metrics not available in data
                        if (!isMetricAvailableInData(metric)) return false;

                        // Search filter
                        if (!metric || !debouncedSearchTerm.trim()) return true;

                        const formattedName = formatMetricName(metric).toLowerCase();
                        const searchTermLower = debouncedSearchTerm.toLowerCase();

                        // Search only in formatted display name
                        return formattedName.includes(searchTermLower);
                      })
                      .map((metric) => {
                        const isChecked = selectedMetrics.some(m =>
                          m && metric && formatMetricString(m) === formatMetricString(metric)
                        );
                        return (
                          <SortableColumnItem
                            key={`${exchangeData.id}-${metric}`}
                            metric={metric}
                            isChecked={isChecked}
                          />
                        );
                      })}
                  </SortableContext>
                </DndContext>
              </div>
              <div className="custmize-columns-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary button-secondary"
                  onClick={() => handleSelectAll(false)}
                  disabled={tableLoading}
                >
                  Clear All
                </button>
                <button
                  type="button"
                  className="btn btn-primary button-primary"
                  onClick={() => setDropdownOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
          )}
          <button
            type="button"
            className="btn btn-outline-primary button-secondary compare-btn"
            onClick={handleCompareClick}
            disabled={tableLoading || (exchangeData?.lineItems?.length ?? 0) === 0 || rows?.length === 0}
          >
            Compare
          </button>
          <span
            title={tableLoading && activeTabId !== "main" ? "Line item data loading" : ""}
          >
          <button
            className="btn btn-primary button-primary inline-min-width-90"
            type="button"
            onClick={() => handleExportClick(includeAISummary, exchangeReportDataBy)}
            disabled={loading || tableLoading || allTabsEmpty}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : (
              "Export"
            )}
          </button>
          </span>
        </div>
      </div>
      {/* Tab bar — always shown for all exchanges */}
      <ul className="nav nav-tabs report-tab-bar">
        {visibleTabs.map((tab) => (
          <li className="nav-item" key={tab.tabId}>
            <button
              className={`nav-link ${activeTabId === tab.tabId ? "active" : ""}`}
              onClick={() => setActiveTabId(tab.tabId)}
            >
              {tab.tabLabel}
            </button>
          </li>
        ))}
      </ul>
      {activeTabId === "main" ? (
      <>
      {tableLoading ? (
        <TableCustomLoader length={noOfExchanges > 1 ? 1 : 2}
          message={`Almost ready! Finalizing ${tableTitle} data`}
        />
      ) : rows?.length > 0 ? (
        <div className="table-container">
          {!isMobile ? (
            // Desktop table view
            <>
              <div className="table-wrapper custom-scrollbar" ref={topRef}>
                <table {...getTableProps()} className="table table-bordered exchange-table">
                  <thead>
                    {headerGroups.map((headerGroup: any) => {
                      const sortableColumns = ['line_item_name', 'start_date', 'end_date'];

                      return (
                        <tr {...headerGroup.getHeaderGroupProps()} key={`${exchangeData.id}-${headerGroup.id}`}>
                          {headerGroup.headers.map((column: any) => {
                            const isSortable = sortableColumns.some(col =>
                              formatMetricString(col) === formatMetricString(column.id)
                            );

                            return (
                              <th
                                {...column.getHeaderProps()}
                                key={`${exchangeData.id}-${column.id}`}
                                className="sortable-header"
                                onClick={() => isSortable && handleSort(column.id)}
                                style={{
                                  ...column.getHeaderProps().style,
                                  cursor: isSortable ? 'pointer' : 'default'
                                }}
                              >
                                {column.render("Header")}
                                {isSortable && sortBy === column.id && (
                                  <i
                                    className={`fas ${sortOrder === 'desc' ? "fa-sort-down inline-sort-icon-desc" : "fa-sort-up inline-sort-icon-asc"}`}
                                  ></i>
                                )}
                                {column.canResize && (
                                  <div
                                    {...column.getResizerProps()}
                                    className="resizer"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {rows.map((row: any) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()} key={`${exchangeData.id}-${row.id}`}>
                          {row.cells.map((cell: any) => (
                            <td {...cell.getCellProps()} key={`${exchangeData.id}-${row.id}-${cell.column.id}`}>
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

            </>
          ) : (
            <div className="mobile-cards-container">
              {rows.map((row: any) => {
                prepareRow(row);
                return (
                  <div className="mobile-card card" key={`${exchangeData.id}-${row.id}`}>
                    {row.cells.map((cell: any) => (
                      <div className="mobile-card-row" key={`${exchangeData.id}-${row.id}-${cell.column.id}`}>
                        <span className="row-title">{cell.column.Header}:</span>
                        <span className="row-value">{cell.render("Cell")}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            currentCount={rows.length}
            pageSize={itemsPerPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      ) : (
        // No data available state
        <div
          className="d-flex justify-content-center align-items-center no-records"
          style={{ minHeight: noOfExchanges > 1 ? "150px" : "200px" }}
        >No data available</div>
      )}
      </>
      ) : (
        (() => {
          const activeTab = visibleTabs.find(t => t.tabId === activeTabId);
          return activeTab && activeTab.tableComponent ? (
            <ConnectedTab
              tabDefinition={activeTab}
              exchangeId={exchangeData?.id}
              requestContext={tabRequestContext}
            />
          ) : null;
        })()
      )}
    </div>
    {compareState.openExchange === tableTitle && (
      <ComparePanel />
    )}
    </>
  );
};

export default ExchangeTable;
